import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { announcementApi } from '../../services/announcementApi';
import {
    PlusCircle,
    Edit,
    Trash,
    AlertCircle,
    CheckCircle,
    Power,
    Pin,
    Eye,
    Megaphone,
    Clock,
    Users,
    Building2,
    Shield,
    Search,
    RefreshCcw,
    Calendar,
    ExternalLink,
    X,
    LayoutDashboard
} from 'lucide-react';
import AnnouncementForm from '../../components/announcements/AnnouncementForm';
import InfoModal from '../../components/common/InfoModal';

const getLifecycleKey = (item) => {
    const status = item.status;
    if (status === 0 || status === 'Draft') return 'draft';
    if (!item.isActive || status === 2 || status === 'Passive') return 'passive';
    if (item.isExpired) return 'expired';
    if (item.isScheduled || (item.publishStartAt && new Date(item.publishStartAt) > new Date())) return 'scheduled';
    return 'live';
};

const getLifecycleBadge = (item) => {
    const key = getLifecycleKey(item);
    switch (key) {
        case 'live':
            return {
                label: 'Yayında',
                dot: 'bg-emerald-500',
                cls: 'bg-emerald-500/15 text-emerald-700 ring-1 ring-inset ring-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-300 dark:ring-emerald-400/30'
            };
        case 'scheduled':
            return {
                label: 'Zamanlanmış',
                dot: 'bg-sky-500',
                cls: 'bg-sky-500/15 text-sky-700 ring-1 ring-inset ring-sky-500/30 dark:bg-sky-500/20 dark:text-sky-300 dark:ring-sky-400/30'
            };
        case 'draft':
            return {
                label: 'Taslak',
                dot: 'bg-amber-500',
                cls: 'bg-amber-500/15 text-amber-800 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300 dark:ring-amber-400/30'
            };
        case 'expired':
            return {
                label: 'Süresi Dolmuş',
                dot: 'bg-rose-500',
                cls: 'bg-rose-500/15 text-rose-700 ring-1 ring-inset ring-rose-500/30 dark:bg-rose-500/20 dark:text-rose-300 dark:ring-rose-400/30'
            };
        default:
            return {
                label: 'Pasif',
                dot: 'bg-slate-400',
                cls: 'bg-slate-500/15 text-slate-700 ring-1 ring-inset ring-slate-400/30 dark:bg-slate-700/60 dark:text-slate-300 dark:ring-slate-600'
            };
    }
};

const getPriorityBadge = (priority = 0) => {
    if (priority >= 20) {
        return {
            label: 'Kritik',
            cls: 'bg-rose-500/15 text-rose-700 ring-1 ring-inset ring-rose-500/30 dark:bg-rose-500/20 dark:text-rose-300'
        };
    }
    if (priority >= 10) {
        return {
            label: 'Yüksek',
            cls: 'bg-amber-500/15 text-amber-800 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/20 dark:text-amber-300'
        };
    }
    return {
        label: 'Normal',
        cls: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
    };
};

const FILTER_TABS = [
    { id: 'all', label: 'Tümü' },
    { id: 'live', label: '🟢 Yayında' },
    { id: 'scheduled', label: '⏳ Zamanlanmış' },
    { id: 'pinned', label: '📌 Sabitlenenler' },
    { id: 'draft', label: '📝 Taslak' },
    { id: 'inactive', label: '⏸️ Pasif / Süresi Dolmuş' },
];

const AnnouncementsPage = () => {
    const { isAuthenticated, permissions } = useAuth();

    const [announcements, setAnnouncements] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);

    const [lifecycleFilter, setLifecycleFilter] = useState('all');
    const [targetFilter, setTargetFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [editingAnnouncement, setEditingAnnouncement] = useState(null);
    const [previewAnnouncement, setPreviewAnnouncement] = useState(null);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [announcementToDeleteId, setAnnouncementToDeleteId] = useState(null);

    const canManage = useMemo(() => permissions.includes('announcements:manage'), [permissions]);

    const fetchAnnouncements = useCallback(async () => {
        if (!canManage) {
            setError('Duyuruları yönetme yetkiniz bulunmamaktadır.');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const data = await announcementApi.list();
            setAnnouncements(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Duyurular yüklenemedi. Lütfen tekrar deneyin.');
        } finally {
            setIsLoading(false);
        }
    }, [canManage]);

    useEffect(() => {
        if (!isFormModalOpen && !isConfirmModalOpen && isAuthenticated) {
            fetchAnnouncements();
        }
    }, [isFormModalOpen, isConfirmModalOpen, isAuthenticated, fetchAnnouncements]);

    // KPI İstatistikleri
    const stats = useMemo(() => {
        let live = 0;
        let scheduled = 0;
        let pinned = 0;
        let draft = 0;
        let inactive = 0;

        announcements.forEach((item) => {
            const key = getLifecycleKey(item);
            if (key === 'live') live += 1;
            else if (key === 'scheduled') scheduled += 1;
            else if (key === 'draft') draft += 1;
            else inactive += 1;

            if (item.pinned) pinned += 1;
        });

        return {
            total: announcements.length,
            live,
            scheduled,
            pinned,
            draft,
            inactive
        };
    }, [announcements]);

    // Filtrelenmiş Liste
    const filteredAnnouncements = useMemo(() => {
        return announcements.filter((item) => {
            const key = getLifecycleKey(item);

            if (lifecycleFilter === 'live' && key !== 'live') return false;
            if (lifecycleFilter === 'scheduled' && key !== 'scheduled') return false;
            if (lifecycleFilter === 'pinned' && !item.pinned) return false;
            if (lifecycleFilter === 'draft' && key !== 'draft') return false;
            if (lifecycleFilter === 'inactive' && key !== 'passive' && key !== 'expired') return false;

            const tType = item.targetType === 'AllCompany' ? 1 : item.targetType === 'Departments' ? 2 : item.targetType === 'Roles' ? 3 : Number(item.targetType);
            if (targetFilter !== 'all' && tType !== Number(targetFilter)) return false;

            if (searchQuery.trim()) {
                const q = searchQuery.trim().toLocaleLowerCase('tr-TR');
                const title = String(item.title || '').toLocaleLowerCase('tr-TR');
                const summary = String(item.summary || '').toLocaleLowerCase('tr-TR');
                const content = String(item.content || '').toLocaleLowerCase('tr-TR');
                const depts = (item.departmentNames || []).join(' ').toLocaleLowerCase('tr-TR');
                const roles = (item.roleNames || []).join(' ').toLocaleLowerCase('tr-TR');
                if (!title.includes(q) && !summary.includes(q) && !content.includes(q) && !depts.includes(q) && !roles.includes(q)) {
                    return false;
                }
            }

            return true;
        });
    }, [announcements, lifecycleFilter, targetFilter, searchQuery]);

    const showBannerMessage = (msg) => {
        setMessage(msg);
        setTimeout(() => setMessage(null), 3500);
    };

    const handleAddClick = () => {
        setEditingAnnouncement(null);
        setIsFormModalOpen(true);
        setMessage(null);
        setError(null);
    };

    const handleEditClick = (announcement) => {
        setMessage(null);
        setError(null);
        setEditingAnnouncement(announcement);
        setIsFormModalOpen(true);
    };

    const handleDeleteRequest = (id) => {
        setAnnouncementToDeleteId(id);
        setIsConfirmModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        setIsLoading(true);
        setError(null);
        setMessage(null);
        try {
            await announcementApi.remove(announcementToDeleteId);
            showBannerMessage('Duyuru başarıyla silindi.');
        } catch (err) {
            setError(err.message || 'Duyuru silinemedi.');
        } finally {
            setIsLoading(false);
            setAnnouncementToDeleteId(null);
            setIsConfirmModalOpen(false);
        }
    };

    const handleToggleActive = async (id) => {
        try {
            await announcementApi.toggleActive(id);
            await fetchAnnouncements();
            showBannerMessage('Duyuru yayın durumu güncellendi.');
        } catch (err) {
            setError('Durum değiştirilemedi.');
            setTimeout(() => setError(null), 3000);
        }
    };

    const handleTogglePinned = async (id) => {
        try {
            await announcementApi.togglePinned(id);
            await fetchAnnouncements();
            showBannerMessage('Sabitleme durumu güncellendi.');
        } catch (err) {
            setError('Sabitleme durumu değiştirilemedi.');
            setTimeout(() => setError(null), 3000);
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            setIsLoading(true);
            if (formData.id) {
                await announcementApi.update(formData.id, formData);
                showBannerMessage('Duyuru başarıyla güncellendi.');
            } else {
                await announcementApi.create(formData);
                showBannerMessage('Duyuru başarıyla oluşturuldu.');
            }
            setIsFormModalOpen(false);
            return Promise.resolve();
        } catch (err) {
            const errorMsg = `Duyuru kaydedilemedi: ${err.message || 'Hata oluştu'}`;
            setError(errorMsg);
            return Promise.reject(new Error(errorMsg));
        } finally {
            setIsLoading(false);
        }
    };

    const handleModalClose = () => {
        setIsFormModalOpen(false);
        setIsConfirmModalOpen(false);
        setEditingAnnouncement(null);
        setAnnouncementToDeleteId(null);
        setError(null);
    };

    const renderTargetCell = (row) => {
        const t = row.targetType;
        if (t === 1 || t === 'AllCompany') {
            return (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500/10 px-2.5 py-1 text-xs font-bold text-indigo-700 ring-1 ring-inset ring-indigo-500/20 dark:bg-indigo-500/20 dark:text-indigo-300">
                    <Users className="h-3.5 w-3.5" />
                    Tüm Firma
                </span>
            );
        }
        if (t === 2 || t === 'Departments') {
            const names = row.departmentNames || [];
            const preview = names.slice(0, 2);
            const rest = names.length - preview.length;
            return (
                <div className="flex flex-wrap items-center gap-1">
                    <span className="inline-flex items-center gap-1 rounded-lg bg-violet-500/10 px-2 py-0.5 text-[11px] font-bold text-violet-700 ring-1 ring-inset ring-violet-500/20 dark:bg-violet-500/20 dark:text-violet-300">
                        <Building2 className="h-3 w-3" />
                        {names.length === 0 ? `${(row.departmentIds || []).length} Departman` : preview.join(', ')}
                        {rest > 0 ? ` +${rest}` : ''}
                    </span>
                </div>
            );
        }
        if (t === 3 || t === 'Roles') {
            const names = row.roleNames || [];
            const preview = names.slice(0, 2);
            const rest = names.length - preview.length;
            return (
                <div className="flex flex-wrap items-center gap-1">
                    <span className="inline-flex items-center gap-1 rounded-lg bg-fuchsia-500/10 px-2 py-0.5 text-[11px] font-bold text-fuchsia-700 ring-1 ring-inset ring-fuchsia-500/20 dark:bg-fuchsia-500/20 dark:text-fuchsia-300">
                        <Shield className="h-3 w-3" />
                        {names.length === 0 ? `${(row.roleIds || []).length} Rol` : preview.join(', ')}
                        {rest > 0 ? ` +${rest}` : ''}
                    </span>
                </div>
            );
        }
        return <span className="text-xs text-slate-400">—</span>;
    };

    if (!canManage && !isLoading) {
        return <div className="p-6 text-center font-bold text-rose-600">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</div>;
    }

    return (
        <div className="min-h-screen w-full space-y-5 bg-slate-50 p-6 dark:bg-gray-900">
            {/* Başlık & Yeni Duyuru Aksiyonu */}
            <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-slate-200/80 bg-white px-6 py-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-600/10 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400">
                        <Megaphone className="h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">
                            Kurumsal Duyuru Merkezi
                        </h1>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Web panosu ve mobil uygulama için hedefli duyuruları ve anlık bildirimleri yönetin
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        type="button"
                        onClick={fetchAnnouncements}
                        disabled={isLoading}
                        className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                        title="Yenile"
                    >
                        <RefreshCcw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                        type="button"
                        onClick={handleAddClick}
                        className="inline-flex h-9 items-center gap-2 rounded-xl bg-indigo-600 px-4 text-xs font-bold text-white shadow-sm transition hover:bg-indigo-700 active:scale-[0.98]"
                    >
                        <PlusCircle className="h-4 w-4" />
                        Yeni Duyuru Oluştur
                    </button>
                </div>
            </div>

            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <div className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Toplam Duyuru</div>
                    <div className="mt-1 text-xl font-bold tabular-nums text-slate-900 dark:text-white">{stats.total}</div>
                </div>
                <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/40 px-4 py-3 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/20">
                    <div className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">Yayında (Aktif)</div>
                    <div className="mt-1 text-xl font-bold tabular-nums text-emerald-800 dark:text-emerald-200">{stats.live}</div>
                </div>
                <div className="rounded-2xl border border-sky-200/80 bg-sky-50/40 px-4 py-3 shadow-sm dark:border-sky-900/50 dark:bg-sky-950/20">
                    <div className="text-[11px] font-semibold text-sky-700 dark:text-sky-300">Zamanlanmış</div>
                    <div className="mt-1 text-xl font-bold tabular-nums text-sky-800 dark:text-sky-200">{stats.scheduled}</div>
                </div>
                <div className="rounded-2xl border border-amber-200/80 bg-amber-50/40 px-4 py-3 shadow-sm dark:border-amber-900/50 dark:bg-amber-950/20">
                    <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Sabitlenen (Pinned)</div>
                    <div className="mt-1 text-xl font-bold tabular-nums text-amber-800 dark:text-amber-200">{stats.pinned}</div>
                </div>
                <div className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Taslak / Pasif / Biten</div>
                    <div className="mt-1 text-xl font-bold tabular-nums text-slate-700 dark:text-slate-300">{stats.draft + stats.inactive}</div>
                </div>
            </div>

            {/* Bildirim / Hata Mesajları */}
            {message && (
                <div className="flex items-center gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50/90 px-4 py-3 text-xs font-semibold text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
                    <CheckCircle className="h-4 w-4 shrink-0" />
                    <span>{message}</span>
                </div>
            )}
            {error && (
                <div className="flex items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50/90 px-4 py-3 text-xs font-semibold text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-200">
                    <div className="flex items-center gap-2.5">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setError(null)}
                        className="rounded-lg px-2 py-0.5 text-[11px] font-bold hover:bg-rose-100 dark:hover:bg-rose-900/40"
                    >
                        Kapat
                    </button>
                </div>
            )}

            {/* Filtre Çubuğu + Duyuru Tablosu */}
            <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 px-5 py-3.5 dark:border-slate-800">
                    {/* Durum Sekmeleri */}
                    <div className="flex flex-wrap items-center gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
                        {FILTER_TABS.map((tab) => {
                            const active = lifecycleFilter === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setLifecycleFilter(tab.id)}
                                    className={`rounded-lg px-2.5 py-1.5 text-xs font-bold transition ${
                                        active
                                            ? 'bg-white text-slate-900 shadow-sm dark:bg-indigo-600 dark:text-white'
                                            : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Hedef Filtresi + Arama */}
                    <div className="flex flex-wrap items-center gap-2">
                        <select
                            value={targetFilter}
                            onChange={(e) => setTargetFilter(e.target.value)}
                            className="h-9 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-700 outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        >
                            <option value="all">Tüm Hedef Kitleler</option>
                            <option value="1">Tüm Firma</option>
                            <option value="2">Departman Hedefli</option>
                            <option value="3">Rol Hedefli</option>
                        </select>

                        <div className="flex h-9 w-64 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 dark:border-slate-700 dark:bg-slate-800">
                            <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Başlık, içerik veya hedef ara..."
                                className="w-full bg-transparent text-xs text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-200"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Tablo */}
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-left">
                        <thead>
                            <tr className="border-b border-slate-200/80 bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400">
                                <th className="px-5 py-3">Duyuru Bilgisi</th>
                                <th className="px-4 py-3">Durum</th>
                                <th className="px-4 py-3">Hedef Kitle</th>
                                <th className="px-4 py-3">Yayın Takvimi</th>
                                <th className="px-4 py-3">Önem & Sabitleme</th>
                                <th className="px-5 py-3 text-right">Aksiyonlar</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center text-xs font-semibold text-slate-400">
                                        Duyurular yükleniyor...
                                    </td>
                                </tr>
                            ) : filteredAnnouncements.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 text-slate-400">
                                            <Megaphone className="h-8 w-8 opacity-40" />
                                            <div className="text-xs font-bold">Kriterlere uygun duyuru bulunamadı</div>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredAnnouncements.map((row) => {
                                    const lifecycle = getLifecycleBadge(row);
                                    const prio = getPriorityBadge(row.priority);
                                    return (
                                        <tr
                                            key={row.id}
                                            className="group transition hover:bg-slate-50/80 dark:hover:bg-slate-800/50"
                                        >
                                            {/* Duyuru Başlık + Özet */}
                                            <td className="max-w-md px-5 py-3.5">
                                                <div className="flex items-start gap-2.5">
                                                    {row.pinned && (
                                                        <span
                                                            title="En Üste Sabitlenmiş Duyuru"
                                                            className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-amber-500/15 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400"
                                                        >
                                                            <Pin className="h-3 w-3" />
                                                        </span>
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="truncate text-sm font-bold text-slate-900 dark:text-white">
                                                                {row.title}
                                                            </span>
                                                            <span className="font-mono text-[10px] text-slate-400">
                                                                #{row.id}
                                                            </span>
                                                        </div>
                                                        <p className="mt-0.5 line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
                                                            {row.summary || row.content}
                                                        </p>
                                                        {row.createdByUserName && (
                                                            <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">
                                                                Oluşturan: {row.createdByUserName}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Yaşam Döngüsü Durumu */}
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="flex flex-col items-start gap-1">
                                                    <span className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold ${lifecycle.cls}`}>
                                                        <span className={`h-1.5 w-1.5 rounded-full ${lifecycle.dot}`} />
                                                        {lifecycle.label}
                                                    </span>
                                                    {!row.showOnDashboard && (
                                                        <span className="text-[10px] font-medium text-slate-400">
                                                            Panoda gizli
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Hedef Kitle */}
                                            <td className="px-4 py-3.5">
                                                {renderTargetCell(row)}
                                            </td>

                                            {/* Yayın Takvimi */}
                                            <td className="px-4 py-3.5 whitespace-nowrap text-xs text-slate-600 dark:text-slate-300">
                                                <div className="flex flex-col gap-0.5">
                                                    <div className="flex items-center gap-1 font-medium">
                                                        <Clock className="h-3 w-3 text-slate-400" />
                                                        <span>
                                                            {row.publishStartAt
                                                                ? new Date(row.publishStartAt).toLocaleString('tr-TR', {
                                                                      day: '2-digit',
                                                                      month: 'short',
                                                                      year: 'numeric',
                                                                      hour: '2-digit',
                                                                      minute: '2-digit'
                                                                  })
                                                                : 'Hemen'}
                                                        </span>
                                                    </div>
                                                    <div className="text-[11px] text-slate-400 dark:text-slate-500">
                                                        Bitiş:{' '}
                                                        {row.publishEndAt
                                                            ? new Date(row.publishEndAt).toLocaleString('tr-TR', {
                                                                  day: '2-digit',
                                                                  month: 'short',
                                                                  year: 'numeric',
                                                                  hour: '2-digit',
                                                                  minute: '2-digit'
                                                              })
                                                            : 'Süresiz'}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Öncelik & Sabitleme */}
                                            <td className="px-4 py-3.5 whitespace-nowrap">
                                                <div className="flex items-center gap-1.5">
                                                    <span className={`rounded-lg px-2 py-0.5 text-[11px] font-bold ${prio.cls}`}>
                                                        {prio.label}
                                                    </span>
                                                    {row.pinned && (
                                                        <span className="rounded-lg bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:text-amber-300">
                                                            Sabitli
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Aksiyonlar */}
                                            <td className="px-5 py-3.5 text-right whitespace-nowrap">
                                                <div className="inline-flex items-center justify-end gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPreviewAnnouncement(row)}
                                                        className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                                        title="Duyuru Önizlemesi"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleTogglePinned(row.id)}
                                                        className={`rounded-lg p-1.5 transition ${
                                                            row.pinned
                                                                ? 'bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-400'
                                                                : 'text-slate-400 hover:bg-slate-100 hover:text-amber-600 dark:hover:bg-slate-800'
                                                        }`}
                                                        title={row.pinned ? 'Sabitlemeyi Kaldır' : 'En Üste Sabitle'}
                                                    >
                                                        <Pin className="h-4 w-4" />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleActive(row.id)}
                                                        className={`rounded-lg p-1.5 transition ${
                                                            row.isActive && row.status !== 0 && row.status !== 'Draft'
                                                                ? 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40'
                                                                : 'text-slate-400 hover:bg-slate-100 hover:text-emerald-600 dark:hover:bg-slate-800'
                                                        }`}
                                                        title={
                                                            row.isActive && row.status !== 0 && row.status !== 'Draft'
                                                                ? 'Pasife Çek'
                                                                : 'Yayına Al (Aktifleştir)'
                                                        }
                                                    >
                                                        <Power className="h-4 w-4" />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditClick(row)}
                                                        className="rounded-lg p-1.5 text-indigo-600 transition hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950/40"
                                                        title="Düzenle"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>

                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteRequest(row.id)}
                                                        className="rounded-lg p-1.5 text-rose-600 transition hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                                                        title="Sil"
                                                    >
                                                        <Trash className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Ekle / Düzenle Form Modalı */}
            <InfoModal
                isOpen={isFormModalOpen}
                onClose={handleModalClose}
                title={editingAnnouncement ? 'Duyuruyu Düzenle' : 'Yeni Kurumsal Duyuru'}
                className="max-w-3xl"
                type="form"
            >
                <AnnouncementForm
                    announcement={editingAnnouncement}
                    onSubmit={handleFormSubmit}
                    onCancel={handleModalClose}
                    isNew={!editingAnnouncement}
                    submitting={isLoading}
                />
            </InfoModal>

            {/* Önizleme Modalı */}
            {previewAnnouncement && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
                    <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
                        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-800">
                            <div>
                                <div className="flex flex-wrap items-center gap-2">
                                    {previewAnnouncement.pinned && (
                                        <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                                            <Pin className="h-3 w-3" /> Sabitli
                                        </span>
                                    )}
                                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${getLifecycleBadge(previewAnnouncement).cls}`}>
                                        {getLifecycleBadge(previewAnnouncement).label}
                                    </span>
                                </div>
                                <h3 className="mt-1.5 text-lg font-bold text-slate-900 dark:text-white">
                                    {previewAnnouncement.title}
                                </h3>
                                <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <Calendar className="h-3.5 w-3.5" />
                                    <span>
                                        {new Date(previewAnnouncement.publishStartAt || previewAnnouncement.createdAt).toLocaleDateString('tr-TR', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </span>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPreviewAnnouncement(null)}
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-3 overflow-y-auto p-6">
                            {previewAnnouncement.summary && previewAnnouncement.summary !== previewAnnouncement.content && (
                                <div className="rounded-xl border border-slate-200/80 bg-slate-50 px-4 py-3 text-xs font-medium text-slate-700 dark:border-slate-800 dark:bg-slate-800/60 dark:text-slate-300">
                                    {previewAnnouncement.summary}
                                </div>
                            )}
                            <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-200">
                                {previewAnnouncement.content}
                            </div>
                        </div>

                        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50/70 px-6 py-3.5 dark:border-slate-800 dark:bg-slate-900/60">
                            <div>{renderTargetCell(previewAnnouncement)}</div>
                            <div className="flex items-center gap-2">
                                {previewAnnouncement.actionUrl && previewAnnouncement.actionButtonText && (
                                    <a
                                        href={previewAnnouncement.actionUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-indigo-700"
                                    >
                                        {previewAnnouncement.actionButtonText}
                                        <ExternalLink className="h-3.5 w-3.5" />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setPreviewAnnouncement(null)}
                                    className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                                >
                                    Kapat
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Silme Onay Modalı */}
            <InfoModal
                isOpen={isConfirmModalOpen}
                onClose={handleModalClose}
                title="Duyuru Silme Onayı"
                message="Bu duyuruyu tamamen silmek istediğinizden emin misiniz? İlgili uygulama içi bildirimler de kaldırılacaktır."
                type="confirm"
                onConfirm={handleDeleteConfirm}
                confirmText="Evet, Sil"
                cancelText="Vazgeç"
            />
        </div>
    );
};

export default AnnouncementsPage;
